/**
 * Toast Notification Utilities
 *
 * Centralized toast notifications using react-hot-toast.
 * Provides consistent, user-friendly error/success messages.
 */

import { toast } from 'react-hot-toast';
import {
  getErrorMessages,
  getApiErrorMessage,
  isValidationError,
} from './errorHandling';

const TOAST_MULTILINE_STYLE = {
  maxWidth: '420px',
  whiteSpace: 'pre-line' as const,
};

function buildToastText(title: string, messages: string[]): string {
  if (messages.length === 0) return title;

  if (messages.length === 1) {
    return messages[0] === title ? title : `${title}\n${messages[0]}`;
  }

  return `${title}\n${messages.map((message, index) => `${index + 1}. ${message}`).join('\n')}`;
}

function showTitledToast(
  variant: 'error' | 'success' | 'info' | 'warning',
  title: string,
  messages: string[],
  duration: number,
) {
  const text = buildToastText(title, messages);
  const options = { duration, style: TOAST_MULTILINE_STYLE };

  switch (variant) {
    case 'error':
      toast.error(text, options);
      break;
    case 'success':
      toast.success(text, options);
      break;
    case 'warning':
      toast(text, { ...options, icon: '⚠️' });
      break;
    default:
      toast(text, options);
  }
}

/**
 * Show error toast notification.
 * Handles validation errors, API errors, and generic failures.
 */
export function showErrorToast(error: unknown, customTitle?: string) {
  const messages = getErrorMessages(error);
  const title =
    customTitle ||
    (isValidationError(error) ? 'Please fix the following' : 'Something went wrong');

  showTitledToast('error', title, messages, isValidationError(error) ? 6000 : 5000);
}

/**
 * Convenience helper for catch blocks: toast.error with API-aware messaging.
 */
export function toastApiError(
  error: unknown,
  fallbackTitle = 'Something went wrong',
  fallbackMessage?: string,
) {
  const messages = getErrorMessages(error);
  const resolvedMessages =
    messages.length === 1 && messages[0] === 'An unexpected error occurred. Please try again.'
      ? [fallbackMessage ?? messages[0]]
      : messages;

  showTitledToast(
    'error',
    fallbackTitle,
    resolvedMessages,
    isValidationError(error) ? 6000 : 5000,
  );
}

/**
 * Show success toast notification.
 */
export function showSuccessToast(message: string, description?: string) {
  if (description) {
    showTitledToast('success', message, [description], 3000);
    return;
  }

  toast.success(message, { duration: 3000 });
}

/**
 * Show info toast notification.
 */
export function showInfoToast(message: string, description?: string) {
  if (description) {
    showTitledToast('info', message, [description], 3000);
    return;
  }

  toast(message, { duration: 3000 });
}

/**
 * Show warning toast notification.
 */
export function showWarningToast(message: string, description?: string) {
  if (description) {
    showTitledToast('warning', message, [description], 4000);
    return;
  }

  toast(message, { duration: 4000, icon: '⚠️' });
}

/**
 * Show loading toast (returns toast id for dismissal).
 */
export function showLoadingToast(message = 'Loading...') {
  return toast.loading(message);
}

/**
 * Dismiss a specific toast.
 */
export function dismissToast(toastId: string) {
  toast.dismiss(toastId);
}

/**
 * Show promise toast (automatically handles loading/success/error states).
 */
export function showPromiseToast<T>(
  promise: Promise<T>,
  messages: {
    loading: string;
    success: string | ((data: T) => string);
    error: string | ((error: unknown) => string);
  },
) {
  return toast.promise(promise, {
    loading: messages.loading,
    success:
      typeof messages.success === 'function'
        ? messages.success
        : () => messages.success as string,
    error: (error: unknown) => {
      if (typeof messages.error === 'function') {
        return messages.error(error);
      }

      return getApiErrorMessage(error, messages.error);
    },
  });
}
